'use client';

import React, { useEffect, useRef } from 'react';
import type { Editor } from 'grapesjs';

interface GrapesJSBlocksPanelProps {
  editor: Editor | null;
}

const GrapesJSBlocksPanel: React.FC<GrapesJSBlocksPanelProps> = ({ editor }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!editor || !containerRef.current) return;
    
    try {
      // The correct way to access the blocks manager
      const blockManager = editor.BlockManager;
      
      // Clear container first
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      
      // Add onClick handlers to all blocks except Stripe
      blockManager.getAll().each(block => {
        // Skip setting our custom handler for Stripe button
        if (block.get('id') === 'stripe-button') {
          return;
        }
        
        block.set('onClick', (block, editor) => {
          const content = block.get('content');
          
          // Get canvas dimensions
          const canvas = editor.Canvas;
          const frame = canvas.getFrame();
          if (!frame || !frame.view) return;
          
          const frameEl = frame.view.el;
          const frameWidth = frameEl.offsetWidth;
          const frameHeight = frameEl.offsetHeight;
          
          // Add component to wrapper
          const component = editor.addComponents(content)[0];
          
          if (component) {
            // Select the component
            editor.select(component);
            
            try {
              // Position in center of visible canvas
              // Use attributes instead of styles to avoid overriding component properties
              // This preserves the component's draggability
              const compStyle = component.getStyle();
              if (!component.view) return;
              const compEl = component.view.el;
              
              // Calculate dimensions
              const compWidth = compEl.offsetWidth || 100;
              const compHeight = compEl.offsetHeight || 100;
              
              // Calculate center position
              const centerX = Math.max(0, (frameWidth - compWidth) / 2);
              const centerY = Math.max(0, frameEl.scrollTop + (frameHeight - compHeight) / 2);
              
              // Position component
              if (!compStyle.position || compStyle.position === 'static') {
                component.addStyle({ position: 'relative' });
              }
              
              // Move to center using the editor's move command
              editor.runCommand('core:component-offset', {
                component,
                x: centerX,
                y: centerY
              });
              
              // Make sure component is visible
              setTimeout(() => {
                if (compEl) {
                  compEl.scrollIntoView({
                    behavior: 'auto',
                    block: 'center'
                  });
                }
              }, 100);
            } catch (e) {
              console.error('Error positioning component:', e);
            }
          }
        });
      });
      
      // Get all blocks and render them
      const allBlocks = blockManager.getAll();
      const renderedBlocks = blockManager.render(allBlocks.models);
      if (renderedBlocks) {
        containerRef.current.appendChild(renderedBlocks);
      }
      
    } catch (error) {
      console.error("Error rendering blocks panel:", error);
      // Show error message in the container
      if (containerRef.current) {
        containerRef.current.innerHTML = 
          `<div class="flex items-center justify-center h-full text-red-400">
            Error loading blocks panel
          </div>`;
      }
    }
    
    // Cleanup function
    return function cleanup() {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [editor]);
  
  return (
    <div ref={containerRef} className="h-full overflow-auto bg-[#1E1E1E] text-white p-2">
      {!editor && (
        <div className="flex items-center justify-center h-full text-gray-400">
          Editor not initialized
        </div>
      )}
    </div>
  );
};

export default GrapesJSBlocksPanel;